import {
  Box,
  Dialog,
  Input,
  UseDisclosureReturn,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { Command, useCommandState } from "cmdk"
import { ComponentProps, ReactNode, useEffect, useState } from "react"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import PWAdminCmdKAuth from "./PWAdminCmdKAuth"
import PWAdminCmdKBlocks from "./PWAdminCmdKBlocks"
import { usePWAdminCmdKContext } from "./PWAdminCmdKContext"
import PWAdminCmdKOperations from "./PWAdminCmdKGraphQLOperations"
import PWAdminCmdKItem, { KeyControlHandler } from "./PWAdminCmdKItem"
import PWAdminCmdKItems from "./PWAdminCmdKItems"
import PWAdminCmdKPackages from "./PWAdminCmdKPackages"
import { PWAdminCmdKOption, PWAdminCmdKOptionWithKeyControl } from "./types"
import useIsPWAdminUtilEnabled from "./useIsPWAdminUtilEnabled"
import { usePWAdminSSOOptions } from "./usePWAdminSSOLink"
import useSupportInfo from "./useSupportInfo"

function SearchStateHook({
  onSearchChange
}: {
  onSearchChange: (search: string) => void
}) {
  const search = useCommandState((state) => state.search)

  // This hook is used to trigger re-renders when the search value changes.
  useEffect(() => {
    onSearchChange(search)
  }, [search])

  return null
}

export const PWAdminCmdKMenu = ({
  children,
  ...cmdProps
}: {
  children: ReactNode | ReactNode[]
} & ComponentProps<typeof Command>) => {
  const [search, setSearch] = useState("")

  return (
    <Command
      className="PWAdminCmdKMenu"
      label="Command Menu"
      // Only filter when searching. This allows to choose only render certain items
      // when searching, yet those items will still be indexed for search. Whereas,
      // if these items weren't rendered initially, they wouldn't be indexed (and thus,
      // unsearchable).
      shouldFilter={!!search}
      loop
      {...cmdProps}
    >
      <SearchStateHook onSearchChange={setSearch} />
      <VStack
        w="full"
        alignItems="stretch"
        p={4}
        bg="black"
        borderRadius="card"
      >
        <Input
          asChild
          placeholder="Search..."
          color="whiteAlpha.500"
          borderColor="whiteAlpha.200"
          _focus={{ bgColor: "whiteAlpha.200" }}
          _placeholder={{ color: "whiteAlpha.500" }}
        >
          <Command.Input />
        </Input>
        <Command.List>
          <Box as={Command.Empty} px={2} py={1} color="whiteAlpha.500">
            No results found.
          </Box>
          {children}
        </Command.List>
      </VStack>
    </Command>
  )
}

export const PWAdminCmdKModal = ({ open, onClose }: UseDisclosureReturn) => {
  const { me } = usePathwrightContext()
  const hasStaffAccess = !!me?.permissions?.has_pathwright_staff_access
  const supportInfo = useSupportInfo()
  const pwAdminSSOOptions = usePWAdminSSOOptions()
  const { hintsActive, setHintsActive } = usePWAdminCmdKContext()

  const hintOption: PWAdminCmdKOption = {
    label: "Hints",
    icon: "lightbulb",
    active: hintsActive,
    onSelect: () => setHintsActive(!hintsActive)
  }

  const [pages, setPages] = useState<("operations" | "packages")[]>([])
  const page = pages[pages.length - 1]

  // Aggregate all options that have keyboard controls and rendering a KeyControlHandler for each.
  // This way, we setup key controls for all top-level options without opening the modal.
  // Previously, we used a different, possibly less-hacky, approach where we rendered the modal but
  // visually hid it so that the top-level key controls would be registered, but now with chakra v3,
  // the Dialog does more to prevent interation of other elements outside the Dialog when open,
  // so we need to do it this way.
  const options = [supportInfo, ...pwAdminSSOOptions, hintOption]
  const keyControls = options
    .filter(
      (option): option is PWAdminCmdKOptionWithKeyControl =>
        "keyControl" in option
    )
    .map((option) => (
      <KeyControlHandler key={option.keyControl} option={option} />
    ))

  return (
    <>
      {keyControls}
      <Dialog.Root
        open={open}
        onOpenChange={(e: { open: boolean }) => !e.open && onClose()}
        trapFocus={open}
        preventScroll={open}
        closeOnEscape={!page}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="transparent" borderRadius="card">
            <Dialog.Body p={0}>
              <PWAdminCmdKMenu
                onKeyDown={(e) => {
                  // Escape goes to previous page
                  // Backspace goes to previous page when search is empty
                  if (
                    page &&
                    e.key === "Escape"
                    // || (e.key === 'Backspace' && !search)
                  ) {
                    setPages((pages) => pages.slice(0, -1))
                  }
                }}
              >
                {!page && (
                  <>
                    <PWAdminCmdKItem option={supportInfo} />
                    <PWAdminCmdKItems options={pwAdminSSOOptions} />
                    <PWAdminCmdKAuth />
                    <PWAdminCmdKItem option={hintOption} />
                    <PWAdminCmdKItem
                      option={{
                        label: "GraphQL Operations",
                        icon: "nested-list",
                        onSelect: () =>
                          setPages((pages) => [...pages, "operations"])
                      }}
                    />
                    {hasStaffAccess && (
                      <PWAdminCmdKItem
                        option={{
                          label: "Packages",
                          icon: "sparkles",
                          onSelect: () =>
                            setPages((pages) => [...pages, "packages"])
                        }}
                      />
                    )}
                    <PWAdminCmdKBlocks />
                  </>
                )}

                {page === "operations" && <PWAdminCmdKOperations />}
                {page === "packages" && <PWAdminCmdKPackages />}
              </PWAdminCmdKMenu>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  )
}

const ConditionallyEnabledPWAdminCmdK = () => {
  const disclosure = useDisclosure()
  const { open, onToggle } = disclosure
  const { checkPWAdminEnabled } = usePWAdminCmdKContext()
  const isPWAdminUtilEnabled = useIsPWAdminUtilEnabled()

  // Toggle the menu via keyboard control.
  // NOTE: the path navigator uses the Meta+k keyboard shortcut,
  // hence the Shift.
  // Including other keyboard shortcuts that may conflict less with other
  // apps and such (Notion desktop app globally intercepts Meta+Shift+K).
  useKeyControl(["Meta+Shift+K", "Control+Shift+K"], onToggle)

  useEffect(() => {
    if (open && !isPWAdminUtilEnabled) {
      // Attempt to check if enabled, which may fail.
      // We're getting flaky results from this check, where sometimes it may work
      // and other times it may not, due to not properly being able to access local
      // storeage across domains.
      checkPWAdminEnabled()
    }
  }, [open, isPWAdminUtilEnabled])

  return isPWAdminUtilEnabled ? <PWAdminCmdKModal {...disclosure} /> : null
}

export default ConditionallyEnabledPWAdminCmdK
